import { useState } from 'react';
import { hasAdminAccess } from '../../lib/roles';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store';
import { authService } from '../../services/auth.service';
import { Button } from '../ui/Button';

interface AuthenticatedSessionWarningProps {
  reason: string;
}

export const AuthenticatedSessionWarning = ({ reason }: AuthenticatedSessionWarningProps) => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleSignOutAndContinue = async () => {
    setIsSigningOut(true);
    try {
      await authService.logout();
    } finally {
      clearAuth();
      setIsSigningOut(false);
    }
  };

  const handleCancel = () => {
    navigate(hasAdminAccess(user?.role) ? '/dashboard' : '/home', { replace: true });
  };

  return (
    <div className="rounded-lg border border-status-warning bg-status-warning-subtle p-4 mb-6">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-status-warning flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-status-warning">
            You're signed in as <span className="font-medium">{user?.email}</span>. To {reason}, you need to sign out first.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              variant="primary"
              onClick={handleSignOutAndContinue}
              isLoading={isSigningOut}
            >
              Sign out and continue
            </Button>
            <Button variant="secondary" onClick={handleCancel} disabled={isSigningOut}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
